import { useTranslation } from "gatsby-plugin-react-i18next";

import { useCountry } from "@func-core/core/application/hooks/useCountry";
import {
  AVAILABLE_COUNTRIES,
  LANDING_CONFIG,
  LandingCountryConfig,
} from "@func-core/core/domain/constants";
import { CountryCode } from "@settings/countries";
import { PageId } from "@settings/pages";
import { shouldShowChatLink } from "@utils/locale-configuration-utils";

import DogAndCatSvg from "@images/templates/pre-sales/dog-and-cat.svg";
import SyringeSvg from "@images/templates/pre-sales/syringe.svg";
import UmbrellaSvg from "@images/templates/pre-sales/umbrella.svg";
import WorldSvg from "@images/templates/pre-sales/world.svg";

interface KspList {
  description?: string;
  title: string;
  items?: string[];
  image: { file: { url: string } };
}

interface landingConfig extends Omit<LandingCountryConfig, "kspList"> {
  faqItems: { title?: string; text?: string }[];
  normalizedCountryCode: CountryCode;
  hasAlternativeBenefitsSection: boolean;
  landingBottomCta: boolean;
  alternateVersions: { id: string; locale: string; slug: string }[];
  kspList: KspList[];
}

export const useLanding = (): landingConfig => {
  const { t, i18n } = useTranslation();
  const { country } = useCountry();

  const normalizedCountryCode = country.language.code.toLocaleLowerCase() as CountryCode;
  const faqTranslations = i18n.getResourceBundle(country.language.code, "translation") as
    | Record<string, any>
    | undefined;

  const showChatLink = shouldShowChatLink(country.iso as CountryCode, PageId.landing);

  const alternateVersions = Object.values(AVAILABLE_COUNTRIES).map(({ iso }) => ({
    id: iso,
    locale: iso.toLowerCase(),
    slug: `/${iso.toLowerCase()}/`,
  }));

  const faqItems = faqTranslations
    ? Object.entries(faqTranslations)
        .filter(([key]) => key.startsWith("landing.faqs.item_"))
        .reduce((acc, [key, value]) => {
          const itemNumber = key.match(/item_(\d+)/)?.[1];
          const type = key.endsWith(".title") ? "title" : "text";

          if (itemNumber) {
            const index = parseInt(itemNumber, 10) - 1;
            acc[index] = acc[index] || {};
            acc[index][type] = value;
          }

          return acc;
        }, [] as Array<{ title?: string; text?: string }>)
        .filter((item) => item.title && item.text)
    : [];

  const hasAlternativeBenefitsSection = country.iso === "DE";

  const landingConfig = LANDING_CONFIG[country.iso as CountryCode];
  const kspList: KspList[] = landingConfig.kspList.map((ksp) => {
    const imageMap = {
      world: WorldSvg,
      syringe: SyringeSvg,
      dogAndCat: DogAndCatSvg,
      umbrella: UmbrellaSvg,
    };

    const mappedUrl = imageMap[ksp.image] as string;

    if (!mappedUrl) {
      throw new Error(`Unknown ksp.image key: ${ksp.image}`);
    }

    const image = {
      file: { url: mappedUrl },
    };

    return {
      ...ksp,
      title: t(ksp.title),
      description: ksp.description ? t(ksp.description) : undefined,
      items: ksp.items ? t(ksp.items).split("\n") : undefined,
      image,
    };
  });

  return {
    normalizedCountryCode,
    faqItems,
    hasAlternativeBenefitsSection,
    showChatLink,
    alternateVersions,
    ...landingConfig,
    kspList,
  };
};
